import React, { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import ProtectedRoute from '../Utils/ProtectedRoute';
import PublicRoute from '../Utils/PublicRoute';
// Auth Components
import Landing from '../AuthComponents/Landing/Landing';
import Login from '../AuthComponents/Login/Login';
import Signup from '../AuthComponents/Signup/Signup';
import RoleSelection from '../AuthComponents/RoleSelection/RoleSelection';
import ForgotPassword from '../AuthComponents/ForgotPassword/ForgotPassword';
import OTPVerification from '../AuthComponents/OTPVerification/OTPVerification';
import LoginOTPVerification from '../AuthComponents/LoginOTPVerification/LoginOTPVerification';
import ResetPassword from '../AuthComponents/ResetPassword/ResetPassword';
import TenantDocumentUpload from '../AuthComponents/TenantDocumentUpload/TenantDocumentUpload';
import CompletePayment from '../AuthComponents/Payment/CompletePayment';
// Landlord Components
import { Dashboard as LandlordDashboard, DashboardItems as LandlordDashboardItems, Settings as LandlordSettings, Profile, ChangePassword, Notification as LandlordNotification, SubscriptionDetail, CallCredit, CallCreditPayment, BillingUsage, MyProperties, PropertyDetail, TenantDetail, BackgroundCheckReport, AddProperty, Leases as LandlordLeases, LeaseDetail as LandlordLeaseDetail } from '../LandlordComponent';
// Tenant Components
import { Dashboard as TenantDashboard, MyProperties as TenantMyProperties, PropertyDetail as TenantPropertyDetail, AutomatedTenantScreening, TenantScreeningPayment, RentSubscriptionPayment, MyScreenings, ScreeningDetail, MyLeases as TenantMyLeases, LeaseDetail as TenantLeaseDetail, Settings as TenantSettings, Profile as TenantProfile, Documents as TenantDocuments, ChangePassword as TenantChangePassword, BackgroundCheck as TenantBackgroundCheck, MyBackgroundCheckReport as TenantBackgroundCheckReport, Notification as TenantNotification } from '../TenantComponent';
// Layouts
import LandlordLayout from '../Layouts/LandlordLayout/LandlordLayout';
import TenantLayout from '../Layouts/TenantLayout/TenantLayout';
// Public pages
import StartCall from '../PublicPages/StartCall';
import Terms from '../PublicPages/Terms';
import AdminRoutes from '../admin/AdminRoutes';

// Lazy load public shareable property page (loads only when visiting /share/:id)
const PublicPropertyDetail = lazy(() => import('../PublicPages/PublicPropertyDetail').then(m => ({ default: m.default })));

export default function Routing() {
  return (
    <>
      <PublicRoute>
        <Routes>
          {/* Public Routes - Redirect to dashboard if logged in */}
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/choose-role" element={<RoleSelection />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/verify-otp" element={<OTPVerification />} />
          <Route path="/verify-login-otp" element={<LoginOTPVerification />} />
          <Route path="/complete-payment" element={<CompletePayment />} />
          <Route path="/tenant/upload-documents" element={<TenantDocumentUpload />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/share/:id" element={
            <Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>}>
              <PublicPropertyDetail />
            </Suspense>
          } />
          <Route path="/start-call" element={<StartCall />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Terms />} />

          {/* Admin panel */}
          <Route path="/admin/*" element={<AdminRoutes />} />

          {/* Protected Landlord Routes with Layout */}
        <Route
          path="/landlord"
          element={
            <ProtectedRoute userType="landlord">
              <LandlordLayout />      
            </ProtectedRoute>
          }
        >
          <Route path="dashboard" element={<LandlordDashboard />} />
          <Route path="dashboard/items" element={<LandlordDashboardItems />} />
          <Route path="properties" element={<MyProperties />} />
          <Route path="properties/add" element={<AddProperty />} />
          <Route path="properties/edit/:id" element={<AddProperty />} />
          <Route path="properties/:id" element={<PropertyDetail />} />
          <Route path="properties/:id/tenant/:tenantId" element={<TenantDetail />} />
          <Route path="properties/:id/tenant/:tenantId/background-check" element={<BackgroundCheckReport />} />
          <Route path="leases" element={<LandlordLeases />} />
          <Route path="leases/:id" element={<LandlordLeaseDetail />} />
          <Route path="notifications" element={<LandlordNotification />} />
          <Route path="settings/call-credit/top-up/:planId" element={<CallCreditPayment />} />
          <Route path="settings" element={<LandlordSettings />}>
            <Route index element={<Profile />} />
            <Route path="change-password" element={<ChangePassword />} />
            <Route path="notification" element={<Navigate to="/landlord/notifications" replace />} />
            <Route path="subscription" element={<SubscriptionDetail />} />
            <Route path="call-credit" element={<CallCredit />} />
            <Route path="billing-usage" element={<BillingUsage />} />
          </Route>
        </Route>

        {/* Protected Tenant Routes with Layout */}
        <Route
          path="/tenant"
          element={
            <ProtectedRoute userType="tenant">
              <TenantLayout />
            </ProtectedRoute>
          }
        >
          <Route path="dashboard" element={<TenantDashboard />} />
          <Route path="properties" element={<TenantMyProperties />} />
          <Route path="properties/:id" element={<TenantPropertyDetail />} />
          <Route path="properties/:id/screening" element={<AutomatedTenantScreening />} />
          <Route path="properties/:id/screening/complete-payment" element={<TenantScreeningPayment />} />
          <Route path="properties/:id/rent/complete-payment" element={<RentSubscriptionPayment />} />
          <Route path="screenings" element={<MyScreenings />} />
          <Route path="screenings/:id" element={<ScreeningDetail />} />
          <Route path="leases" element={<TenantMyLeases />} />
          <Route path="leases/:id" element={<TenantLeaseDetail />} />
          <Route path="background-check/report" element={<TenantBackgroundCheckReport />} />
          <Route path="notifications" element={<TenantNotification />} />
          <Route path="settings" element={<TenantSettings />}>
            <Route index element={<TenantProfile />} />
            <Route path="document" element={<TenantDocuments />} />
            <Route path="change-password" element={<TenantChangePassword />} />
            <Route path="background-check" element={<TenantBackgroundCheck />} />
            <Route path="notification" element={<Navigate to="/tenant/notifications" replace />} />
          </Route>
        </Route>


          {/* Default redirect */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </PublicRoute>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </>
  );
}
